import { useMemo, useState } from "react";
import { GetMapsLocation } from "../../APIService/Maps";
import LocationSelectorDropdown, {
    LocationDropDownObject,
    LocationDropdownTypeEnum
} from "./LocationSelectorDropdown";

type LocationViewProps = {
    theme: string;
    userLocation: GetMapsLocation;
    allLocations: GetMapsLocation[];
    onLocationChange: (location: GetMapsLocation) => void;
};

type LocationLevel =
    | "Country"
    | "City"
    | "Site"
    | "Building"
    | "Floor"
    | "Zone";

const hierarchy: LocationLevel[] = [
    "Country",
    "City",
    "Site",
    "Building",
    "Floor",
    "Zone"
];

const LocationView = ({
    theme,
    userLocation,
    allLocations,
    onLocationChange
}: LocationViewProps) => {

    // Filter state per dropdown (Country ignored)
    const [filters, setFilters] = useState<Record<LocationLevel, boolean>>({
        Country: true,
        City: true,
        Site: true,
        Building: true,
        Floor: true,
        Zone: true
    });

    // 🔥 Get dropdown values (dedupe + optional filter)
    const getDropDownValues = (
        level: LocationLevel
    ): LocationDropDownObject[] => {

        const isFilterApplied =
            level === "Country" ? true : filters[level];

        let scopedLocations = allLocations;

        if (isFilterApplied && level !== "Country") {

            const levelIndex = hierarchy.indexOf(level);

            scopedLocations = allLocations.filter(loc =>
                hierarchy.slice(0, levelIndex).every(parentKey => {
                    const selectedValue = userLocation[parentKey];
                    if (!selectedValue) return true;
                    return loc[parentKey] === selectedValue;
                })
            );
        }

        // Map defining which levels should be included in subLabel
    const subLabelMap: Record<LocationLevel, LocationLevel[]> = {
        Country: ["City"],
        City: ["Country"],
        Site: ["City"],
        Building: ["City", "Site"],
        Floor: ["City", "Building"],
        Zone: ["City", "Building", "Floor"]
    };

        // Remove duplicates
        const unique = new Map<string, LocationDropDownObject>();

        scopedLocations.forEach(loc => {
            const value = loc[level];
            if (!value) return;

            if (!unique.has(value)) {
            const subLevels = subLabelMap[level];
            const subLabel = subLevels
                .map(parent => loc[parent])
                .filter(Boolean)
                .join(", ");

            unique.set(value, { label: value, subLabel });
        }
        });

        return Array.from(unique.values());
    };

    // 🔥 Handle Selection
    const handleLocationSelect = (
        value: string,
        type: LocationDropdownTypeEnum
    ) => {

        const matched = allLocations.find((location) => location[type] === value);
        onLocationChange(matched ?? userLocation);
    };

    const handleFilterChange = (
        level: LocationLevel,
        checked: boolean
    ) => {
        console.log("Filter Change: ", checked);
        setFilters(prev => ({
            ...prev,
            [level]: checked
        }));
    };

    return (
        <div style={{ display: "flex", gap: 5, alignItems: "center" }}>
            {hierarchy.map(level => (
                <div key={level} style={{ width: "max-content" }}>
                    {getDropDownValues(level).length > 0 && (
                        <LocationSelectorDropdown
                            theme={theme}
                            value={userLocation[level]}
                            placeHolder={level}
                            type={LocationDropdownTypeEnum[level]}
                            dropDownList={getDropDownValues(level)}
                            isFilterEnabled={level !== "Country"}
                            isFilterChecked={filters[level] ?? true}
                            onFilterChange={handleFilterChange}
                            onSelect={handleLocationSelect}
                        />
                    )}
                </div>
            ))}
        </div>
    );
};

export default LocationView;
